import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Shield, Building2, MapPin, Lock, Eye, EyeOff, CheckCircle2, XCircle, HelpCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Password requirements
const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'Almeno 8 caratteri', test: (pwd) => pwd.length >= 8 },
  { id: 'uppercase', label: 'Almeno una lettera maiuscola (A-Z)', test: (pwd) => /[A-Z]/.test(pwd) },
  { id: 'lowercase', label: 'Almeno una lettera minuscola (a-z)', test: (pwd) => /[a-z]/.test(pwd) },
  { id: 'number', label: 'Almeno un numero (0-9)', test: (pwd) => /[0-9]/.test(pwd) },
  { id: 'special', label: 'Almeno un carattere speciale (!@#$%^&*)', test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd) }
];

const Profilo = () => {
  const { user, token } = useAuth();
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const fetchUserDetails = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users/me`, { headers });
      setUserDetails(response.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
      // Use basic user info from auth context
      setUserDetails(user);
    } finally {
      setLoading(false);
    }
  };

  // Check if all password requirements are met
  const checkPasswordRequirements = (password) => {
    return PASSWORD_REQUIREMENTS.every(req => req.test(password));
  };

  // Get individual requirement status
  const getRequirementStatus = (requirement) => {
    return requirement.test(newPassword);
  };

  // Handle password change
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    // Validate current password is provided
    if (!currentPassword) {
      setPasswordError('Inserisci la password attuale');
      return;
    }

    // Validate new password meets requirements
    if (!checkPasswordRequirements(newPassword)) {
      setPasswordError('La nuova password non soddisfa tutti i requisiti');
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError('Le password non corrispondono');
      return;
    }

    // Validate new password is different from current
    if (currentPassword === newPassword) {
      setPasswordError('La nuova password deve essere diversa da quella attuale');
      return;
    }

    setChangingPassword(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers });

      toast.success('Password modificata con successo');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      const message = error.response?.data?.detail || 'Errore nel cambio password';
      setPasswordError(message);
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#3BA99C]" />
      </div>
    );
  }

  const displayUser = userDetails || user;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-slate-900" data-testid="profile-title">
          Il Mio Profilo
        </h1>
        <p className="text-slate-500 mt-2">Visualizza i tuoi dati e gestisci la password</p>
      </div>

      {/* Profile Info Card */}
      <Card data-testid="profile-info-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-[#3BA99C]" />
            Informazioni Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nome */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                <User className="w-4 h-4" />
                Nome Completo
              </label>
              <p className="text-lg font-semibold text-slate-900" data-testid="profile-name">
                {displayUser?.full_name || '-'}
              </p>
            </div>

            {/* Email */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                <Mail className="w-4 h-4" />
                Email
              </label>
              <p className="text-lg font-semibold text-slate-900" data-testid="profile-email">
                {displayUser?.email || '-'}
              </p>
            </div>

            {/* Ruolo */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                <Shield className="w-4 h-4" />
                Ruolo
              </label>
              <p className="text-lg" data-testid="profile-role">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                  displayUser?.role === 'super_admin'
                    ? 'bg-red-100 text-red-700'
                    : displayUser?.role === 'admin'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-slate-100 text-slate-700'
                }`}>
                  {displayUser?.role === 'super_admin' ? (
                    <>
                      <Shield className="w-4 h-4" />
                      Super Amministratore
                    </>
                  ) : displayUser?.role === 'admin' ? (
                    <>
                      <Shield className="w-4 h-4" />
                      Amministratore
                    </>
                  ) : (
                    <>
                      <User className="w-4 h-4" />
                      Utente
                    </>
                  )}
                </span>
              </p>
            </div>

            {/* CAA - solo per utenti non admin */}
            {displayUser?.role !== 'admin' && displayUser?.role !== 'super_admin' && (
              <>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    CAA
                  </label>
                  <p className="text-lg font-semibold text-slate-900" data-testid="profile-caa">
                    {displayUser?.caa_name || displayUser?.caa_id || '-'}
                  </p>
                </div>

                <div className="space-y-1 md:col-span-2">
                  <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    Uffici CAA
                  </label>
                  <div data-testid="profile-ufficio">
                    {displayUser?.uffici_list && displayUser.uffici_list.length > 0 ? (
                      <div className="space-y-2">
                        {displayUser.uffici_list.map((ufficio, index) => (
                          <div 
                            key={ufficio.office_id || index} 
                            className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg"
                          >
                            <MapPin className="w-4 h-4 text-[#3BA99C]" />
                            <span className="font-semibold text-slate-900">{ufficio.name}</span>
                            {ufficio.code && (
                              <span className="text-sm text-slate-500">({ufficio.code})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : displayUser?.ufficio_name ? (
                      <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <MapPin className="w-4 h-4 text-[#3BA99C]" />
                        <span className="font-semibold text-slate-900">{displayUser.ufficio_name}</span>
                        {displayUser.ufficio_code && (
                          <span className="text-sm text-slate-500">({displayUser.ufficio_code})</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-lg font-semibold text-slate-900">-</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card data-testid="change-password-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-600" />
            Modifica Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current Password */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Password Attuale
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Inserisci la password attuale"
                    className="w-full px-4 py-2 pr-10 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nuova Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setPasswordError('');
                    }}
                    placeholder="Inserisci la nuova password"
                    className="w-full px-4 py-2 pr-10 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Conferma Nuova Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPasswordError('');
                    }}
                    placeholder="Ripeti la nuova password"
                    className="w-full px-4 py-2 pr-10 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Requisiti Password
              </h4>
              <ul className="space-y-2">
                {PASSWORD_REQUIREMENTS.map((req) => {
                  const isMet = getRequirementStatus(req);
                  return (
                    <li
                      key={req.id}
                      className={`flex items-center gap-2 text-sm ${
                        newPassword.length > 0
                          ? isMet
                            ? 'text-green-600'
                            : 'text-red-500'
                          : 'text-slate-500'
                      }`}
                      data-testid={`requirement-${req.id}`}
                    >
                      {newPassword.length > 0 ? (
                        isMet ? (
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 flex-shrink-0" />
                        )
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0" />
                      )}
                      {req.label}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Password Match Indicator */}
            {confirmPassword.length > 0 && (
              <div className={`flex items-center gap-2 text-sm ${
                newPassword === confirmPassword ? 'text-green-600' : 'text-red-500'
              }`}>
                {newPassword === confirmPassword ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Le password corrispondono
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Le password non corrispondono
                  </>
                )}
              </div>
            )}

            {/* Error Message */}
            {passwordError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
                <XCircle className="w-4 h-4 flex-shrink-0" />
                {passwordError}
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={changingPassword || !checkPasswordRequirements(newPassword) || newPassword !== confirmPassword || !currentPassword}
                className="px-6 py-2 bg-[#3BA99C] hover:bg-[#2D8A7F] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Modifica in corso...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Modifica Password
                  </>
                )}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Support Card */}
      <Card data-testid="support-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-600" />
            Supporto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-600 mb-4">
            Per assistenza tecnica o domande sull'utilizzo della piattaforma, contatta il nostro team di supporto.
          </p>
          <a
            href="mailto:hd@etom.it"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
            data-testid="support-email-link"
          >
            <Mail className="w-5 h-5" />
            hd@etom.it
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profilo;
